import { useCallback, useMemo, useState } from 'react'
import { MessageSquare, Star, Eye, EyeOff, Trash2 } from 'lucide-react'
import { PageHeader } from '@/shell/AppShell'
import { DataTable } from '@/components/DataTable'
import type { AdminColumn, RowAction } from '@/components/DataTable/types'
import { PERMISSIONS } from '@/lib/permissions'
import { StatusBadge, type StatusKind } from '@/components/StatusBadge'
import { RelativeTime } from '@/components/DataDisplay'
import { DangerDialog } from '@/components/DangerDialog'
import { useToast } from '@/providers/UiProviders'
import { fetchAppReviews, moderateReview, APP_MOD_STR as S, type AppReviewRow } from './appModApi'

/**
 * A5.23 — Reyting va sharhlar moderatsiyasi. GET /admin/mini-apps/reviews (lane-A
 * greenfield → fail-soft bo'sh). Ko'rsatish/yashirish/o'chirish → POST
 * /admin/mini-apps/reviews/:id/moderate {decision}.
 */
export function AppReviewsScreen() {
  const { toast } = useToast()
  const [deleteTarget, setDeleteTarget] = useState<AppReviewRow | null>(null)
  const [reloadKey, setReloadKey] = useState(0)

  const run = useCallback(
    async (fn: () => Promise<unknown>) => {
      try {
        await fn()
        toast(S.reviewModerated, { tone: 'success' })
        setReloadKey((k) => k + 1)
      } catch (e) {
        toast(S.failed, { tone: 'error', ...(e instanceof Error && e.message ? { description: e.message } : {}) })
        throw e
      }
    },
    [toast],
  )

  const statusKind = (s: string): StatusKind => (s === 'visible' ? 'active' : s === 'hidden' ? 'deleted' : 'pending')
  const statusLabel = (s: string) => (s === 'visible' ? S.visible : s === 'hidden' ? S.hidden : S.pending)

  const columns = useMemo<AdminColumn<AppReviewRow>[]>(
    () => [
      { key: 'app_title', header: S.app, type: 'text', minWidth: 160 },
      { key: 'user', header: S.user, type: 'text', width: 150 },
      {
        key: 'rating',
        header: S.rating,
        type: 'number',
        width: 110,
        render: (r) => (
          <span className="inline-flex items-center gap-1 text-warning">
            <Star size={13} className="fill-current" />
            <span className="tabular text-primary">{r.rating}</span>
          </span>
        ),
      },
      {
        key: 'text',
        header: S.review,
        type: 'longtext',
        minWidth: 240,
        render: (r) => <span className="block max-w-md truncate text-secondary">{r.text || '—'}</span>,
      },
      {
        key: 'status',
        header: S.status,
        type: 'text',
        width: 120,
        render: (r) => <StatusBadge status={statusKind(r.status)} label={statusLabel(r.status)} />,
      },
      {
        key: 'created_at',
        header: S.created,
        type: 'date',
        width: 150,
        render: (r) => <RelativeTime value={r.created_at} className="text-secondary" />,
      },
    ],
    [],
  )

  const rowActions = useMemo<RowAction<AppReviewRow>[]>(
    () => [
      {
        id: 'show',
        label: S.show,
        icon: <Eye size={13} />,
        visible: (r) => r.status !== 'visible',
        onSelect: (r) => void run(() => moderateReview(r.id, 'approve')),
      },
      {
        id: 'hide',
        label: S.hide,
        icon: <EyeOff size={13} />,
        danger: true,
        visible: (r) => r.status === 'visible',
        onSelect: (r) => void run(() => moderateReview(r.id, 'hide')),
      },
      {
        id: 'delete',
        label: S.deleteReview,
        icon: <Trash2 size={13} />,
        danger: true,
        onSelect: (r) => setDeleteTarget(r),
      },
    ],
    [run],
  )

  const fetcher = useCallback(fetchAppReviews, [])

  return (
    <>
      <PageHeader title={S.reviewsTitle} screenId="A5.23" />
      <DataTable<AppReviewRow>
        key={reloadKey}
        screenId="A5.23"
        permission={PERMISSIONS.USERS_VIEW}
        columns={columns}
        fetcher={fetcher}
        rowId={(r) => String(r.id)}
        rowActions={rowActions}
        exportable
        emptyState={{ icon: <MessageSquare size={28} strokeWidth={1.5} />, title: S.reviewsEmpty }}
      />

      {deleteTarget && (
        <DangerDialog
          open
          onClose={() => setDeleteTarget(null)}
          screenId="A5.23"
          level={2}
          title={S.deleteReview}
          description={S.reviewsTitle}
          confirmLabel={S.deleteReview}
          onConfirm={async () => {
            await run(() => moderateReview(deleteTarget.id, 'delete'))
            setDeleteTarget(null)
          }}
        />
      )}
    </>
  )
}
